import React from 'react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  AreaChart
} from 'recharts';

// Custom Tooltip Component
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div style={{
        backgroundColor: 'white',
        border: '1px solid #e2e8f0',
        borderRadius: '0.5rem',
        padding: '0.75rem',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
      }}>
        <p style={{ margin: 0, fontWeight: '600', color: '#0f172a', marginBottom: '0.25rem' }}>
          {label}
        </p>
        {payload.map((entry, index) => (
          <p key={index} style={{ margin: 0, color: entry.color, fontSize: '0.875rem' }}>
            {entry.name}: {entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// 1. Risk Score Trend Chart
export const RiskScoreTrendChart = ({ data }) => {
  // Expected data format: [{ month: 'Jan', score: 65 }, ...]
  const defaultData = data || [
    { month: 'Jan', score: 72 },
    { month: 'Feb', score: 68 },
    { month: 'Mar', score: 71 },
    { month: 'Apr', score: 65 },
    { month: 'May', score: 62 },
    { month: 'Jun', score: 58 }
  ];

  return (
    <div style={{
      backgroundColor: 'white',
      border: '1px solid #e2e8f0',
      borderRadius: '0.75rem',
      padding: '1.5rem'
    }}>
      <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#0f172a', marginBottom: '1rem' }}>
        Risk Score Trend
      </h3>
      <ResponsiveContainer width="100%" height={300}>
        <AreaChart data={defaultData}>
          <defs>
            <linearGradient id="colorScore" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#178B9B" stopOpacity={0.3}/>
              <stop offset="95%" stopColor="#178B9B" stopOpacity={0}/>
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
          <XAxis
            dataKey="month"
            stroke="#64748b"
            style={{ fontSize: '0.875rem' }}
          />
          <YAxis
            stroke="#64748b"
            style={{ fontSize: '0.875rem' }}
            domain={[0, 100]}
          />
          <Tooltip content={<CustomTooltip />} />
          <Area
            type="monotone"
            dataKey="score"
            stroke="#178B9B"
            strokeWidth={3}
            fillOpacity={1}
            fill="url(#colorScore)"
          />
        </AreaChart>
      </ResponsiveContainer>
      <p style={{ fontSize: '0.875rem', color: '#64748b', marginTop: '0.5rem', textAlign: 'center' }}>
        Lower scores indicate better property conditions
      </p>
    </div>
  );
};

// 2. Portfolio Risk Distribution (Pie Chart)
export const PortfolioRiskDistributionChart = ({ data }) => {
  // Expected data format: { critical: 2, high: 5, moderate: 8, low: 12 }
  const chartData = [
    { name: 'Critical', value: data?.critical || 0, color: '#dc2626' },
    { name: 'High', value: data?.high || 0, color: '#ea580c' },
    { name: 'Moderate', value: data?.moderate || 0, color: '#f59e0b' },
    { name: 'Low', value: data?.low || 0, color: '#10b981' }
  ].filter(item => item.value > 0); // Only show non-zero values

  const total = chartData.reduce((sum, item) => sum + item.value, 0);

  return (
    <div style={{
      backgroundColor: 'white',
      border: '1px solid #e2e8f0',
      borderRadius: '0.75rem',
      padding: '1.5rem'
    }}>
      <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#0f172a', marginBottom: '1rem' }}>
        Portfolio Risk Distribution
      </h3>
      {total > 0 ? (
        <>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '1.5rem', flexWrap: 'wrap', marginTop: '1rem' }}>
            {chartData.map((item, index) => (
              <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <div style={{ width: 12, height: 12, backgroundColor: item.color, borderRadius: '50%' }} />
                <span style={{ fontSize: '0.875rem', color: '#64748b' }}>
                  {item.name}: {item.value}
                </span>
              </div>
            ))}
          </div>
        </>
      ) : (
        <p style={{ textAlign: 'center', color: '#64748b', padding: '2rem' }}>
          No buildings to display
        </p>
      )}
    </div>
  );
};

// 3. Issues by Category (Bar Chart)
export const IssuesByCategoryChart = ({ data }) => {
  // Expected data format: [{ category: 'Mold', count: 12 }, ...]
  const defaultData = data || [];

  const getColor = (count) => {
    if (count >= 10) return '#dc2626';
    if (count >= 5) return '#ea580c';
    if (count >= 2) return '#f59e0b';
    return '#10b981';
  };

  return (
    <div style={{
      backgroundColor: 'white',
      border: '1px solid #e2e8f0',
      borderRadius: '0.75rem',
      padding: '1.5rem'
    }}>
      <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#0f172a', marginBottom: '1rem' }}>
        Issues by Category
      </h3>
      {defaultData.length > 0 ? (
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={defaultData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis
              dataKey="category"
              stroke="#64748b"
              style={{ fontSize: '0.75rem' }}
              angle={-45}
              textAnchor="end"
              height={80}
            />
            <YAxis
              stroke="#64748b"
              style={{ fontSize: '0.875rem' }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar
              dataKey="count"
              fill="#178B9B"
              radius={[8, 8, 0, 0]}
            >
              {defaultData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={getColor(entry.count)} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <p style={{ textAlign: 'center', color: '#64748b', padding: '2rem' }}>
          No issue data available
        </p>
      )}
    </div>
  );
};

// 4. Geographic Comparison Chart
export const GeographicComparisonChart = ({ data }) => {
  // Expected data format: { thisProperty: 65, quarterMile: 58, oneMile: 62, threeMiles: 68 }
  const chartData = [
    { name: 'This Property', score: data?.thisProperty || 0, color: '#178B9B' },
    { name: '1/4 Mile Avg', score: data?.quarterMile || 0, color: '#64748b' },
    { name: '1 Mile Avg', score: data?.oneMile || 0, color: '#94a3b8' },
    { name: '3 Mile Avg', score: data?.threeMiles || 0, color: '#cbd5e1' }
  ].filter(item => item.score > 0);

  return (
    <div style={{
      backgroundColor: 'white',
      border: '1px solid #e2e8f0',
      borderRadius: '0.75rem',
      padding: '1.5rem'
    }}>
      <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#0f172a', marginBottom: '1rem' }}>
        Geographic Risk Comparison
      </h3>
      {chartData.length > 0 ? (
        <>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis
                type="number"
                stroke="#64748b"
                style={{ fontSize: '0.875rem' }}
                domain={[0, 100]}
              />
              <YAxis
                dataKey="name"
                type="category"
                stroke="#64748b"
                style={{ fontSize: '0.875rem' }}
                width={100}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="score" radius={[0, 8, 8, 0]}>
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <p style={{ fontSize: '0.875rem', color: '#64748b', marginTop: '0.5rem', textAlign: 'center' }}>
            {data?.thisProperty < (data?.oneMile || 0)
              ? '✓ This property performs better than area average'
              : '⚠ This property has higher risk than area average'}
          </p>
        </>
      ) : (
        <p style={{ textAlign: 'center', color: '#64748b', padding: '2rem' }}>
          Geographic comparison requires location data
        </p>
      )}
    </div>
  );
};

// 5. Mini Trend Chart (for cards)
export const MiniTrendChart = ({ data, color = '#178B9B' }) => {
  return (
    <ResponsiveContainer width="100%" height={60}>
      <LineChart data={data}>
        <Line
          type="monotone"
          dataKey="value"
          stroke={color}
          strokeWidth={2}
          dot={false}
        />
      </LineChart>
    </ResponsiveContainer>
  );
};

// 6. Remediation Time Chart
export const RemediationTimeChart = ({ data }) => {
  // Expected data format: [{ month: 'Jan', avgDays: 7 }, ...]
  const defaultData = data || [];

  return (
    <div style={{
      backgroundColor: 'white',
      border: '1px solid #e2e8f0',
      borderRadius: '0.75rem',
      padding: '1.5rem'
    }}>
      <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#0f172a', marginBottom: '1rem' }}>
        Average Remediation Time
      </h3>
      {defaultData.length > 0 ? (
        <>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={defaultData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis
                dataKey="month"
                stroke="#64748b"
                style={{ fontSize: '0.875rem' }}
              />
              <YAxis
                stroke="#64748b"
                style={{ fontSize: '0.875rem' }}
                label={{ value: 'Days', angle: -90, position: 'insideLeft' }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Line
                type="monotone"
                dataKey="avgDays"
                stroke="#10b981"
                strokeWidth={3}
                dot={{ fill: '#10b981', r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
          <p style={{ fontSize: '0.875rem', color: '#64748b', marginTop: '0.5rem', textAlign: 'center' }}>
            Lower remediation times indicate faster response to issues
          </p>
        </>
      ) : (
        <p style={{ textAlign: 'center', color: '#64748b', padding: '2rem' }}>
          No remediation data available yet
        </p>
      )}
    </div>
  );
};

export default {
  RiskScoreTrendChart,
  PortfolioRiskDistributionChart,
  IssuesByCategoryChart,
  GeographicComparisonChart,
  MiniTrendChart,
  RemediationTimeChart
};
